// A taxa que cada plataforma cobra no canal de assessoria.
//
// Até aqui `fee_plat` era uma premissa escalar do escritório: um número
// editável, igual para todo cliente. Na prática ele é TABELADO pela plataforma,
// e numa delas depende do patrimônio do cliente — deixar o consultor digitar 15
// quando a plataforma dele cobra 30 produz um documento com o custo errado sem
// nada ficar vermelho.
//
// Este arquivo é a tabela e a régua de leitura dela. Ele NÃO decide se a taxa
// entra no custo do cliente (isso é o canal, em `constants/baldes.js`) nem como
// ela se reparte (`utils/fee-lab-baldes.js`).
//
// ★ ESPELHO: `aawz-web-backend/src/aawz_fee_fixo/core/plataformas.py`, e
// `test_paridade_plataformas_frontend.py` compara os dois arquivo a arquivo.
// Mexer aqui sem mexer lá deixa o PDF divergindo da tela.

export const CHAVE_PLATAFORMA = 'plataforma'

export const PLATAFORMA_XP = 'xp'
export const PLATAFORMA_BTG = 'btg'
export const PLATAFORMA_SAFRA = 'safra'

// ★ 13/08/2026 (owner): a corretora do cliente não está na tabela, ou o
// consultor quer simular outro valor. Aqui a taxa volta a ser digitada — mas
// como ESCOLHA NOMEADA do cenário, e não como a premissa calada de antes da
// tabelagem.
//
// A diferença com `SEM_PLATAFORMA` é toda: aquele é o estado dos cenários
// ANTIGOS, que não têm o campo; este é um consultor dizendo "a taxa é esta".
// Os dois leem o `fee_plat` do cenário, e é por isso que só a normalização
// separa um do outro — `SEM_PLATAFORMA` é o destino do valor desconhecido,
// `personalizada` sobrevive à normalização e vai ao JSON salvo com nome.
export const PLATAFORMA_PERSONALIZADA = 'personalizada'

// A plataforma dos cenários anteriores a esta decisão. `null` — e não 'xp':
// aqueles cenários gravaram um `fee_plat` digitado, e atribuir-lhes uma
// plataforma faria o documento afirmar uma escolha que o consultor não fez.
export const SEM_PLATAFORMA = null

// Plataformas de taxa única, em bps ao ano sobre o AuC.
export const TAXA_FIXA_BPS = {
  [PLATAFORMA_BTG]: 30,
  [PLATAFORMA_SAFRA]: 30,
}

// A escada da XP, do maior patrimônio para o menor.
//
// A ordem é PRÉ-REQUISITO da leitura ("o primeiro piso que o patrimônio
// alcança"), e por isso `faixasOrdenadas` a valida em vez de confiar nela: uma
// lista fora de ordem devolveria 20 bps para um cliente de R$ 5 mi, calada.
//
// As bordas foram decididas pelo owner em 11/08/2026: **R$ 1 mi cravado paga
// 15** e **R$ 3 mi cravado paga 10** — o piso pertence à faixa de MENOR custo.
// É a leitura que favorece o cliente na borda, e as duas estão travadas por
// teste porque "≥ ou >" é o detalhe que ninguém revisa e todo mundo erra.
export const FAIXAS_XP = [
  [3000000, 10],
  [1000000, 15],
  [0, 20],
]

// A ordem é a do seletor. A personalizada vem por último: ela é a saída para
// quem não se encontra na lista, e ler as tabeladas primeiro é o que faz o
// consultor procurar a corretora dele antes de digitar um número.
export const PLATAFORMAS = [
  PLATAFORMA_XP,
  PLATAFORMA_BTG,
  PLATAFORMA_SAFRA,
  PLATAFORMA_PERSONALIZADA,
]

// A faixa do campo da taxa personalizada, em bps ao ano.
//
// O teto de 100 é folgado de propósito: a maior tabelada cobra 30, e a taxa
// sai de DENTRO do fee do profissional (60 default) — acima disso a perna do
// assessor satura em zero, o que a tela mostra, mas que ninguém contrata. Um
// teto apertado demais impediria a simulação exploratória, que é metade do
// motivo desta opção existir.
export const FAIXA_TAXA_PERSONALIZADA = { min: 0, max: 100, step: 1 }

// A plataforma de um cenário NOVO.
//
// XP, e a razão é aritmética, não preferência: no AuC default de R$ 1 mi a
// tabela dela devolve exatamente os 15 bps que `DEF.fee_plat` já trazia. Quem
// abrir a simulação depois desta mudança vê o MESMO número de antes — a
// tabelagem não reprecifica ninguém por acidente, e trocar de plataforma passa
// a ser um ato explícito do consultor.
//
// Não confundir com `SEM_PLATAFORMA`: aquele é o estado dos cenários ANTIGOS,
// que não têm o campo e continuam valendo pelo `fee_plat` gravado.
export const PLATAFORMA_PADRAO = PLATAFORMA_XP

export const ROTULO_DA_PLATAFORMA = {
  [PLATAFORMA_XP]: 'XP',
  [PLATAFORMA_BTG]: 'BTG',
  [PLATAFORMA_SAFRA]: 'Safra',
  [PLATAFORMA_PERSONALIZADA]: 'Personalizada',
}

const numeroOuNulo = (v) =>
  typeof v === 'number' && Number.isFinite(v) ? v : null

export const faixasOrdenadas = (faixas) => {
  const pisos = faixas.map(([piso]) => piso)
  return pisos.every((piso, i) => i === 0 || pisos[i - 1] > piso)
}

/**
 * A taxa tabelada, em bps ao ano.
 *
 * Devolve `null` quando a plataforma é desconhecida, ou quando falta o
 * patrimônio numa plataforma escalonada — ausência é informação: cair no
 * default de outra plataforma publicaria um custo que o cliente não paga.
 *
 * `personalizada` também sai `null` aqui, e por design: ela não é tabelada, e
 * quem responde por ela é `taxaDoCenario` — a tabela não tem o que dizer sobre
 * um número que o consultor digitou.
 *
 * @param {string|null} plataforma Uma de `PLATAFORMAS`.
 * @param {number} [patrimonioReais] O AuC. Só a XP o consulta.
 * @returns {number|null}
 */
export const taxaDaPlataformaBps = (plataforma, patrimonioReais) => {
  if (Object.prototype.hasOwnProperty.call(TAXA_FIXA_BPS, plataforma)) {
    return TAXA_FIXA_BPS[plataforma]
  }
  if (plataforma !== PLATAFORMA_XP) return null

  const patrimonio = numeroOuNulo(patrimonioReais)
  if (patrimonio === null || patrimonio < 0) return null

  if (!faixasOrdenadas(FAIXAS_XP)) return null
  const faixa = FAIXAS_XP.find(([piso]) => patrimonio >= piso)
  return faixa ? faixa[1] : null
}

/**
 * A plataforma como ela vai para o JSON salvo.
 *
 * Par de `canalNormalizado`, e pelo mesmo motivo: `inputs` é clone do estado,
 * e um valor estranho gravado aqui faria o backend ler uma plataforma que a
 * tabela não conhece — `taxaDaPlataformaBps` devolveria `null` e o documento
 * sairia sem a taxa que a tela mostrou.
 *
 * Valor desconhecido vira `SEM_PLATAFORMA`, e NÃO `PLATAFORMA_PADRAO`: cair no
 * default publicaria uma escolha que o consultor não fez. Com `null`, o
 * cenário volta a valer pelo `fee_plat` gravado, que é o comportamento
 * declarado para quem não escolheu.
 *
 * @param {*} plataforma
 * @returns {string|null}
 */
export const plataformaNormalizada = (plataforma) => {
  return PLATAFORMAS.includes(plataforma) ? plataforma : SEM_PLATAFORMA
}

/**
 * A taxa que ESTE cenário usa — a junta entre a tabela e o cenário salvo.
 *
 * 1. cenário COM `plataforma`: a taxa vem da TABELA, e o `fee_plat` gravado é
 *    ignorado (um valor digitado que contradiga a tabela é o defeito que esta
 *    task veio fechar);
 * 2. cenário SEM `plataforma` — tudo o que foi salvo antes desta decisão —:
 *    vale o `fee_plat` do cenário, intacto. Aqueles documentos foram entregues
 *    com aquele número.
 * 3. cenário `personalizada` (13/08/2026): vale o `fee_plat` digitado, e o
 *    patrimônio não o move. Mesma leitura do caso 2, decisão oposta — ali é o
 *    silêncio de uma época, aqui é o consultor afirmando a taxa.
 */
export const taxaDoCenario = (inputs, premissas, patrimonioReais) => {
  const plataforma = (inputs || {})[CHAVE_PLATAFORMA]
  const semEscolha = plataforma === SEM_PLATAFORMA || plataforma === undefined
  if (semEscolha || plataforma === PLATAFORMA_PERSONALIZADA) {
    return numeroOuNulo((premissas || {}).fee_plat)
  }
  return taxaDaPlataformaBps(plataforma, patrimonioReais)
}
